import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { WarningCircle, Users, ChartLine, ThumbsUp, Plus } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const REASONS = ["compensation", "career_growth", "manager", "work_life_balance", "role_fit", "relocation", "health", "higher_studies", "personal", "other"];

export default function ExitDashboard() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
  const [dash, setDash] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", primary_reason: "career_growth", rating_manager: 3, rating_role: 3, rating_culture: 3, rating_compensation: 3, would_recommend: true, strengths: "", improvements: "", notes: "" });

  const load = () => {
    api.get("/exit-dashboard", { params }).then(({ data }) => setDash(data)).catch(() => {});
    api.get("/exit-interviews", { params }).then(({ data }) => setInterviews(data)).catch(() => {});
    api.get("/employees", { params }).then(({ data }) => setEmployees(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const save = async () => {
    try {
      const payload = { ...form };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      ["rating_manager", "rating_role", "rating_culture", "rating_compensation"].forEach((k) => payload[k] = parseInt(payload[k]));
      await api.post("/exit-interviews", payload);
      toast.success("Exit interview saved");
      setOpen(false);
      setForm({ employee_id: "", primary_reason: "career_growth", rating_manager: 3, rating_role: 3, rating_culture: 3, rating_compensation: 3, would_recommend: true, strengths: "", improvements: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Retention · Exit management</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Exit dashboard</h1>
          <p className="text-sm text-zinc-500 mt-2">Attrition trends, exit-interview insights, and at-risk employees identified from pulse + leave signals.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="ei-add-btn"><Plus size={16} className="mr-1.5" /> Exit interview</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">Record exit interview</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="ei-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Primary reason *</Label>
                <Select value={form.primary_reason} onValueChange={(v) => setForm({ ...form, primary_reason: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="ei-reason-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {["manager", "role", "culture", "compensation"].map((k) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs capitalize">Rating: {k}</Label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} onClick={() => setForm({ ...form, [`rating_${k}`]: n })} data-testid={`ei-${k}-${n}`}
                        className={`w-8 h-8 border rounded-sm text-xs ${form[`rating_${k}`] === n ? "bg-adb-navy text-white border-adb-navy" : "bg-white border-zinc-200"}`}>{n}</button>
                    ))}
                  </div>
                </div>
              ))}
              <label className="flex items-center gap-2 col-span-2 pt-2 cursor-pointer">
                <input type="checkbox" checked={form.would_recommend} onChange={(e) => setForm({ ...form, would_recommend: e.target.checked })} data-testid="ei-recommend" />
                <span className="text-xs">Would recommend Zenith360 HR / this company to a friend</span>
              </label>
              <div className="col-span-2 space-y-1"><Label className="text-xs">What worked well</Label><Textarea value={form.strengths} onChange={(e) => setForm({ ...form, strengths: e.target.value })} className="rounded-sm" rows={2} /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">What to improve</Label><Textarea value={form.improvements} onChange={(e) => setForm({ ...form, improvements: e.target.value })} className="rounded-sm" rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} disabled={!form.employee_id} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="ei-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Exit interviews" value={dash.exit_interviews_total} />
          <Stat label="Would recommend" value={dash.would_recommend_percent != null ? `${dash.would_recommend_percent}%` : "—"} icon={ThumbsUp} accent="text-emerald-700" />
          <Stat label="Avg manager rating" value={dash.average_exit_ratings.manager ?? "—"} sub="/5" />
          <Stat label="At retention risk" value={dash.retention_risk?.length || 0} icon={WarningCircle} accent="text-red-700" />
        </div>
      )}

      <Tabs defaultValue="trends">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="trends" className="rounded-sm"><ChartLine size={14} className="mr-1" /> Trends</TabsTrigger>
          <TabsTrigger value="risk" className="rounded-sm"><WarningCircle size={14} className="mr-1" /> Retention risk</TabsTrigger>
          <TabsTrigger value="interviews" className="rounded-sm"><Users size={14} className="mr-1" /> Interviews</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="mt-4 space-y-4">
          {dash && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-zinc-200 bg-white rounded-sm p-5">
                <div className="font-display font-bold">Monthly exits (12 months)</div>
                <div className="h-64 mt-2">
                  <ResponsiveContainer><LineChart data={dash.monthly_exits}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                    <Line dataKey="exits" stroke="#1a2d59" strokeWidth={2} dot={{ r: 3, fill: "#33cc33" }} />
                  </LineChart></ResponsiveContainer>
                </div>
              </div>
              <div className="border border-zinc-200 bg-white rounded-sm p-5">
                <div className="font-display font-bold">Reasons for leaving</div>
                <div className="h-64 mt-2">
                  <ResponsiveContainer><BarChart data={dash.reasons.filter((r) => r.count > 0)} layout="vertical" margin={{ left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis type="number" fontSize={11} allowDecimals={false} /><YAxis dataKey="reason" type="category" fontSize={10} width={140} />
                    <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#33cc33" radius={[0, 4, 4, 0]} />
                  </BarChart></ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="risk" className="mt-4">
          <div className="border border-zinc-200 bg-white rounded-sm">
            {(!dash || dash.retention_risk.length === 0) && <div className="p-12 text-center text-sm text-zinc-500">No at-risk employees detected. 🎯</div>}
            {dash?.retention_risk.map((r) => (
              <div key={r.employee_id} className="flex items-center gap-3 px-4 py-3 border-b border-zinc-100 last:border-0">
                <WarningCircle size={20} className="text-red-500" weight="duotone" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{r.employee_name}</div>
                  <div className="text-xs text-zinc-500">{r.employee_code} · {r.department}</div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {r.signals.map((s) => (
                    <Badge key={s} variant="outline" className="rounded-sm bg-red-50 text-red-700 border-red-200 text-[10px] uppercase">{s.replace(/_/g, " ")}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="interviews" className="mt-4">
          <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
            <table className="hr-table">
              <thead><tr><th>Employee</th><th>Reason</th><th>Ratings (M/R/C/$)</th><th>Recommend</th><th>Date</th></tr></thead>
              <tbody>
                {interviews.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-zinc-500 text-sm">No interviews recorded.</td></tr>}
                {interviews.map((ei) => (
                  <tr key={ei.id}>
                    <td>{ei.employee_name || "—"}</td>
                    <td className="capitalize text-xs">{ei.primary_reason.replace(/_/g, " ")}</td>
                    <td className="font-mono-plex text-xs">{ei.rating_manager}/{ei.rating_role}/{ei.rating_culture}/{ei.rating_compensation}</td>
                    <td>{ei.would_recommend ? "👍" : "👎"}</td>
                    <td className="font-mono-plex text-xs">{new Date(ei.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
          <div className={`font-display text-3xl font-bold mt-1 ${accent || "text-adb-navy"}`}>{value}{sub && <span className="text-sm text-zinc-400">{sub}</span>}</div>
        </div>
        {Icon && <Icon size={20} className="text-zinc-300" weight="duotone" />}
      </div>
    </div>
  );
}
